import {
  Bank,
  CaretDown,
  CaretLeft,
  CaretUp,
  House,
  Money,
  Storefront,
  TruckTrailer,
  UsersThree,
  ChatDots,
  Handshake,
  SignIn,
} from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenView } from '../../utils/ScreenViewContext';

interface DemoSideNavProps {
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  showSideNav: boolean;
  setShowSideNav: (show: boolean) => void;
  uncategorizedTransactionsCount: number;
}

const demoNavLinks = [
  {
    name: 'Home',
    path: '/demo/home',
    icon: <House size={20} className="mr-2" />,
  },
  {
    name: 'Banking',
    path: '/demo/banking/overview',
    icon: <Money size={20} className="mr-2" />,
    subTabs: [
      { name: 'Overview', path: '/demo/banking/overview' },
      { name: 'Cards', path: '/demo/banking/cards' },
      {
        name: 'Transactions',
        path: '/demo/banking/transactions',
        notificationCount: 2,
      },
      { name: 'Cash Advances', path: '/demo/banking/cash-advances/active' },
      { name: 'Statements', path: '/demo/banking/statements' },
    ],
  },
  {
    name: 'Finances',
    path: '/demo/finances/overview',
    icon: <Bank size={20} className="mr-2" />,
    subTabs: [
      { name: 'Overview', path: '/demo/finances/overview' },
      { name: 'Budgeting', path: '/demo/finances/budgeting' },
      { name: 'Driver Performance', path: '/demo/finances/driver-performance' },
      { name: 'Data Exports', path: '/demo/finances/data-exports' },
    ],
  },
  {
    name: 'Assets',
    path: '/demo/assets',
    icon: <TruckTrailer size={20} className="mr-2" />,
  },
  {
    name: 'Drivers',
    path: '/demo/drivers',
    icon: <UsersThree size={20} className="mr-2" />,
  },
  {
    name: 'Vendor Marketplace',
    path: '',
    icon: <Storefront size={20} className="mr-2" />,
    disabled: true,
  },
];

const DemoSideNav = ({
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  showSideNav,
  setShowSideNav,
  uncategorizedTransactionsCount,
}: DemoSideNavProps) => {
  const [expandedTab, setExpandedTab] = useState<string | null>(null);
  const { screenView } = useScreenView();
  const navigate = useNavigate();

  const [isSideNavVisible, setIsSideNavVisible] = useState(showSideNav);
  const [isAnimating, setIsAnimating] = useState(false);
  const sideNavRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (subTab?.length > 0) {
      setExpandedTab(currentTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);

  useEffect(() => {
    if (showSideNav) {
      setIsSideNavVisible(true);
      setTimeout(() => {
        setIsAnimating(true);
      }, 0);
    } else {
      setIsAnimating(false);
      setTimeout(() => {
        setIsSideNavVisible(false);
      }, 300);
    }
  }, [showSideNav]);

  useEffect(() => {
    if (screenView !== 'desktop' && showSideNav) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          sideNavRef.current &&
          !sideNavRef.current.contains(event.target as Node)
        ) {
          setShowSideNav(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSideNav, screenView, setShowSideNav]);

  const handleTabClick = (tab: any) => {
    setCurrentTab(tab.name);
    if (tab.subTabs) {
      setExpandedTab(expandedTab === tab.name ? null : tab.name);
      navigate(tab.path);
    } else {
      setExpandedTab(null);
      navigate(tab.path);
    }
  };

  const handleSubTabClick = (subTabName: string, path: string) => {
    setSubTab(subTabName);
    navigate(path);
  };

  const renderNavLinks = () => (
    <div className="flex flex-col items-start gap-y-[12px]">
      <div className="flex w-full justify-start border-b-[1px] border-b-[#E6E6E6] p-[12px] pb-[24px]">
        <img alt="tach-logo" src="/TachLogo.svg" className="h-6" />
      </div>
      <nav className="flex w-full flex-col space-y-4 text-sm font-[600]">
        {demoNavLinks.map((link, idx) => (
          <div key={idx} className="w-full">
            <p
              onClick={() => !(link as any).disabled && handleTabClick(link)}
              className={`${(link as any).disabled ? 'cursor-not-allowed text-[#C0C2CC] opacity-50' : currentTab === link?.name && !link.subTabs ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen' : currentTab === link?.name && link.subTabs ? 'text-ctadarkgreen' : 'text-[#858897] hover:bg-gray-100'} flex flex-row justify-between rounded-[6px] p-3 ${!(link as any).disabled && 'hover:cursor-pointer'}`}
            >
              <span className="flex items-center">
                {link?.icon}
                {link?.name}
              </span>
              {link.subTabs &&
                (expandedTab === link.name ? (
                  <CaretUp size={20} />
                ) : (
                  <CaretDown size={20} />
                ))}
            </p>
            {link.subTabs && expandedTab === link.name && (
              <div className="mt-2 space-y-2">
                {link.subTabs.map((sub, subIdx) => (
                  <h2
                    key={subIdx}
                    onClick={() => handleSubTabClick(sub.name, sub.path)}
                    className={`flex items-center justify-between rounded-lg p-2 pl-4 ${
                      subTab === sub.name
                        ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen text-ctadarkgreen'
                        : 'text-[#858897] hover:bg-gray-100'
                    } hover:cursor-pointer`}
                  >
                    <p className="ml-8">{sub.name}</p>
                    {sub.name === 'Transactions' &&
                      uncategorizedTransactionsCount > 0 && (
                        <span className="ml-2 rounded-full bg-orange-400 px-2 py-1 text-xs font-semibold text-white">
                          {uncategorizedTransactionsCount}
                        </span>
                      )}
                  </h2>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </div>
  );

  return (
    <>
      {screenView === 'desktop' ? (
        <div className="flex h-full w-[300px] max-w-[300px] flex-col justify-between overflow-hidden bg-white p-[16px]">
          {renderNavLinks()}
          <div className="space-y-2">
            <button
              onClick={() => navigate('/demo/broker-network')}
              className="flex w-full items-center rounded-[8px] border-[1px] border-blue-500 bg-blue-50 px-6 py-1.5 hover:bg-blue-100"
            >
              <div className="flex flex-row items-center space-x-6">
                <div className="flex w-6 shrink-0 justify-center">
                  <Handshake
                    size={20}
                    className="text-blue-600"
                    weight="bold"
                  />
                </div>
                <div className="text-left">
                  <p className="text-[14px] font-semibold text-blue-900">
                    Promote My Business
                  </p>
                  <p className="text-[12px] text-blue-700">
                    Let TACH advocate for you to your broker network
                  </p>
                </div>
              </div>
            </button>
            <button
              onClick={() =>
                (window.location.href = 'https://tachusa.com/select-role')
              }
              className="flex w-full flex-col items-center justify-center space-y-2 rounded-[8px] border-[1px] border-ctadarkgreen bg-ctadarkgreen p-2 text-white hover:bg-emerald-700"
            >
              <div className="flex flex-row items-center space-x-4">
                <SignIn size={20} weight="bold" />
                <p className="text-[16px] font-semibold">Sign Up</p>
              </div>
            </button>
            <button
              disabled
              className="flex w-full flex-col items-center justify-center space-y-2 rounded-[8px] border-[1px] border-bordergray p-2 opacity-50"
            >
              <div className="flex flex-row items-center space-x-4">
                <ChatDots
                  size={20}
                  className="text-graysecondary"
                  weight="bold"
                />
                <p className="text-[16px] text-graytertiary">Contact Support</p>
              </div>
            </button>
          </div>
        </div>
      ) : (
        isSideNavVisible && (
          <div
            ref={sideNavRef}
            className={`fixed left-0 top-0 z-[999] flex h-screen w-[300px] max-w-[300px] flex-col overflow-hidden border-r-[1px] border-bordergray bg-white transition-transform duration-300 ease-in-out ${
              isAnimating ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            <div className="flex h-[56px] w-full flex-row items-center justify-between border-b-[1px] border-b-[#E6E6E6] p-[12px] px-4">
              <img src="/TachLogo.svg" alt="Tach Logo" className="h-6" />
              <CaretLeft
                size={20}
                onClick={() => setShowSideNav(false)}
                className="hover:cursor-pointer"
              />
            </div>
            <div className="flex flex-1 flex-col overflow-y-auto p-4">
              <nav className="flex w-full flex-col space-y-4 text-sm font-[600]">
                {demoNavLinks.map((link, idx) => (
                  <div key={idx} className="w-full">
                    <p
                      onClick={() =>
                        !(link as any).disabled && handleTabClick(link)
                      }
                      className={`${(link as any).disabled ? 'cursor-not-allowed text-[#C0C2CC] opacity-50' : currentTab === link?.name && !link.subTabs ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen' : currentTab === link?.name && link.subTabs ? 'text-ctadarkgreen' : 'text-[#858897] hover:bg-gray-100'} flex flex-row justify-between rounded-[6px] p-3 ${!(link as any).disabled && 'hover:cursor-pointer'}`}
                    >
                      <span className="flex items-center">
                        {link?.icon}
                        {link?.name}
                      </span>
                      {link.subTabs &&
                        (expandedTab === link.name ? (
                          <CaretUp size={20} />
                        ) : (
                          <CaretDown size={20} />
                        ))}
                    </p>
                    {link.subTabs && expandedTab === link.name && (
                      <div className="mt-2 space-y-2">
                        {link.subTabs.map((sub, subIdx) => (
                          <p
                            key={subIdx}
                            onClick={() =>
                              handleSubTabClick(sub.name, sub.path)
                            }
                            className={`flex items-center justify-between rounded-lg p-2 pl-4 ${
                              subTab === sub.name
                                ? 'border-l-[4px] border-l-ctadarkgreen bg-ctaactivegreen text-ctadarkgreen'
                                : 'text-[#858897] hover:bg-gray-100'
                            } hover:cursor-pointer`}
                          >
                            <h2 className="ml-8">{sub.name}</h2>
                            {sub.notificationCount && (
                              <span className="ml-2 rounded-full bg-orange-400 px-2 py-1 text-xs font-semibold text-white">
                                {sub.notificationCount}
                              </span>
                            )}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
              <div className="mt-auto space-y-2 pt-4">
                <button
                  onClick={() => navigate('/demo/broker-network')}
                  className="flex w-full items-center rounded-[8px] border-[1px] border-blue-500 bg-blue-50 px-6 py-1.5 hover:bg-blue-100"
                >
                  <div className="flex flex-row items-center space-x-6">
                    <div className="flex w-6 shrink-0 justify-center">
                      <Handshake
                        size={20}
                        className="text-blue-600"
                        weight="bold"
                      />
                    </div>
                    <div className="text-left">
                      <p className="text-[14px] font-semibold text-blue-900">
                        Promote My Business
                      </p>
                      <p className="text-[12px] text-blue-700">
                        Let TACH advocate for you to your broker network
                      </p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() =>
                    (window.location.href = 'https://tachusa.com/select-role')
                  }
                  className="flex w-full flex-col items-center justify-center space-y-2 rounded-[8px] border-[1px] border-ctadarkgreen bg-ctadarkgreen p-2 text-white hover:bg-emerald-700"
                >
                  <div className="flex flex-row items-center space-x-4">
                    <SignIn size={20} weight="bold" />
                    <p className="text-[16px] font-semibold">Sign Up</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default DemoSideNav;
